import type { LangMap } from "./index";

/** Report periodici dello studente e condivisione con famiglia. */
const it: Record<string, unknown> = {
  title: "Report periodici",
  subtitle: "Una pagella formativa per ogni studente: andamento, punti di forza, lacune e cosa fare adesso.",
  refresh: "Aggiorna",
  from: "Dal",
  to: "Al",
  generate: "Genera report",
  regenerate: "Rigenera",
  generating: "Lumen sta scrivendo...",
  generated: "Report creato (non ancora condiviso)",
  generateError: "Non sono riuscito a creare il report",
  saved: "Salvato",
  saveError: "Non sono riuscito a salvare",
  saveNote: "Salva nota",
  note: "Nota del docente",
  notePlaceholder: "Una riga per la famiglia: cosa vedi in classe, cosa chiedi a casa.",
  shareStudent: "Condividi con lo studente",
  shareParents: "Condividi con la famiglia",
  print: "Stampa / PDF",
  printTitle: "Report di {{name}}",
  delete: "Elimina",
  strengths: "Punti di forza",
  gaps: "Su cosa lavorare",
  missing: "{{count}} non consegnate",
  missingTitle: "Attività non consegnate",
  line: "Media {{average}} · consegne {{submitted}}/{{expected}}",
  kpi: {
    students: "Studenti",
    average: "Media classe",
    completion: "Consegne",
    atRisk: "Da seguire",
  },
  empty: {
    title: "Nessuno studente nel periodo",
    body: "Cambia le date o aggiungi studenti alla classe per generare i report.",
  },
  family: {
    title: "Report dai docenti",
    subtitle: "I report che gli insegnanti hanno condiviso con voi.",
    empty: "Nessun report condiviso al momento.",
    period: "Periodo",
  },
};

const en: Record<string, unknown> = {
  title: "Periodic reports",
  subtitle: "A formative report for each student: trend, strengths, gaps and next steps.",
  refresh: "Refresh",
  from: "From",
  to: "To",
  generate: "Generate report",
  regenerate: "Regenerate",
  generating: "Lumen is writing...",
  generated: "Report created (not shared yet)",
  generateError: "Couldn't create the report",
  saved: "Saved",
  saveError: "Couldn't save",
  saveNote: "Save note",
  note: "Teacher note",
  notePlaceholder: "One line for the family: what you see in class, what to work on at home.",
  shareStudent: "Share with the student",
  shareParents: "Share with the family",
  print: "Print / PDF",
  printTitle: "Report for {{name}}",
  delete: "Delete",
  strengths: "Strengths",
  gaps: "To work on",
  missing: "{{count}} not submitted",
  missingTitle: "Missing activities",
  line: "Average {{average}} · submissions {{submitted}}/{{expected}}",
  kpi: { students: "Students", average: "Class average", completion: "Submissions", atRisk: "Need support" },
  empty: {
    title: "No students in this period",
    body: "Change the dates or add students to the class to generate reports.",
  },
  family: {
    title: "Reports from teachers",
    subtitle: "The reports teachers have shared with you.",
    empty: "No shared report yet.",
    period: "Period",
  },
};

const es: Record<string, unknown> = {
  ...en,
  title: "Informes periódicos",
  from: "Desde",
  to: "Hasta",
  generate: "Generar informe",
  regenerate: "Regenerar",
  generating: "Lumen está escribiendo...",
  note: "Nota del profesor",
  shareStudent: "Compartir con el alumno",
  shareParents: "Compartir con la familia",
  print: "Imprimir / PDF",
  strengths: "Puntos fuertes",
  gaps: "A mejorar",
  family: { title: "Informes de los profesores", subtitle: "Informes compartidos contigo.", empty: "Aún no hay informes.", period: "Periodo" },
};

const fr: Record<string, unknown> = {
  ...en,
  title: "Bulletins périodiques",
  from: "Du",
  to: "Au",
  generate: "Générer le bulletin",
  regenerate: "Régénérer",
  generating: "Lumen rédige...",
  note: "Note de l'enseignant",
  shareStudent: "Partager avec l'élève",
  shareParents: "Partager avec la famille",
  print: "Imprimer / PDF",
  strengths: "Points forts",
  gaps: "À travailler",
  family: { title: "Bulletins des enseignants", subtitle: "Bulletins partagés avec vous.", empty: "Aucun bulletin partagé.", period: "Période" },
};

const de: Record<string, unknown> = {
  ...en,
  title: "Periodische Berichte",
  from: "Von",
  to: "Bis",
  generate: "Bericht erstellen",
  regenerate: "Neu erstellen",
  generating: "Lumen schreibt...",
  note: "Notiz der Lehrkraft",
  shareStudent: "Mit der Lernenden teilen",
  shareParents: "Mit der Familie teilen",
  print: "Drucken / PDF",
  strengths: "Stärken",
  gaps: "Daran arbeiten",
  family: { title: "Berichte der Lehrkräfte", subtitle: "Mit euch geteilte Berichte.", empty: "Noch keine Berichte.", period: "Zeitraum" },
};

const pt: Record<string, unknown> = {
  ...en,
  title: "Relatórios periódicos",
  from: "De",
  to: "Até",
  generate: "Gerar relatório",
  regenerate: "Gerar de novo",
  generating: "A Lumen está a escrever...",
  note: "Nota do professor",
  shareStudent: "Partilhar com o aluno",
  shareParents: "Partilhar com a família",
  print: "Imprimir / PDF",
  strengths: "Pontos fortes",
  gaps: "A melhorar",
  family: { title: "Relatórios dos professores", subtitle: "Relatórios partilhados convosco.", empty: "Ainda sem relatórios.", period: "Período" },
};

export const report: LangMap = { it, en, es, fr, de, pt };
